import {Alert, Box, Button, CircularProgress, Stack, Typography} from "@mui/material";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {Panel, SoftChip} from "../../../../components/kit";
import React, {useEffect, useState} from "react";
import {useApiManager} from "../../../../components/utils/api";
import {
	DedicationBillingSummary,
	DedicationCreateResultDto,
	DedicationRestControllerApiFactory,
	Occupation,
	RedmineStat
} from "arteco-api-client-ts";
import {fileLocalSave} from "../../../../components/form/form_inputs/form_file";
import DedicationTable from "./dedication/dedication_table";
import AlertTitle from "@mui/material/AlertTitle";
import {useConfirm, usePrompt} from "../../../../components/utils/dialogs";
import {OccupationImputation} from "./occupation_imputation";


export interface OccupationGroupProps {
	year: number;
	month: string;
	occupation: Occupation;
}


export const OccupationGroup = ({month, year, occupation}: OccupationGroupProps) => {

	const apiManager = useApiManager();
	const dedicationCtrl = apiManager.factory(DedicationRestControllerApiFactory);
	const {confirm, confirmDialog} = useConfirm();
	const {prompt, promptDialog} = usePrompt();

	const [operatingBills, setOperatingBills] = useState<boolean>(false);
	const [summary, setSummary] = useState<DedicationBillingSummary | undefined>(undefined);
	const [selectedDedicationIds, setSelectedDedicationIds] = useState<number[]>([]);
	const [createDedications, setCreateDedications] = useState<boolean>(false);
	const [importRedmine, setImportRedmine] = useState<boolean>(false);
	const [repairing, setRepairing] = useState<boolean>(false);
	const [redmineStat, setRedmineStat] = useState<RedmineStat | undefined>(undefined);
	const [createResult, setCreateResult] = useState<DedicationCreateResultDto | undefined>(undefined);
	const [timesWarnings, setTimesWarnings] = useState<string[]>([]);
	const [reloadToken, setReloadToken] = useState<number>(0);

	const dedicable = {selectedDedicationIds, setSelectedDedicationIds};

	const loadBilledPending = () => {
		apiManager.execute(dedicationCtrl.checkBillingPendingDedication(year, parseInt(month)))
		.then(summary => setSummary(summary));
	}

	const loadRedmineStat = () => {
		apiManager.execute(dedicationCtrl.getRedmineDedication(year, parseInt(month)))
		.then(stat => setRedmineStat(stat));
	}

	// Depende también del año: sin él, cambiar de año dejaba el panel de facturas y las stats de
	// Redmine con los datos del año anterior, y los botones de facturar operaban sobre cifras
	// que no correspondían.
	useEffect(() => {
		setSummary(undefined);
		setRedmineStat(undefined);
		setSelectedDedicationIds([]);
		setCreateResult(undefined);
		setTimesWarnings([]);
		setReloadToken(t => t + 1);
		loadBilledPending();
		loadRedmineStat();
	}, [month, year]);

	const borrarFacturas = async () => {
		if (!await confirm({
			title: "Borrar facturas",
			message: "Se borrarán las facturas generadas a partir de las dedicaciones de este mes.",
			confirmLabel: "Borrar facturas",
			danger: true
		})) {
			return;
		}
		setOperatingBills(true);
		apiManager.execute(dedicationCtrl.deleteBillsDedication(year, parseInt(month)))
		.then(bills => {
			loadBilledPending();
			apiManager.enqueueSnackbar(`Se han borrado ${bills.length} facturas`, {variant: "success"});
		})
		.finally(() => setOperatingBills(false));
	}

	const crearFacturas = async () => {
		if (!await confirm({
			title: "Generar facturas",
			message: "Se generarán las facturas agrupando por cliente y grupo de factura."
		})) {
			return;
		}
		setOperatingBills(true);
		apiManager.execute(dedicationCtrl.makeBillsDedication(year, parseInt(month), selectedDedicationIds))
		.then(bills => {
			loadBilledPending();
			apiManager.enqueueSnackbar(`Se han generado ${bills.length} facturas`, {variant: "success"});
			setSelectedDedicationIds([]);
		})
		.finally(() => setOperatingBills(false));
	}

	const duplicarDedicaciones = async () => {
		const value = await prompt({
			title: "Duplicar dedicaciones",
			message: `Se duplicarán las ${selectedDedicationIds.length} dedicaciones seleccionadas al mes que indiques.`,
			label: "Año y mes (aaaa-mm)",
			defaultValue: `${year}-${month.padStart(2, "0")}`,
			confirmLabel: "Duplicar",
			validate: (v) => /^\d{4}-(0[1-9]|1[0-2])$/.test(v.trim()) ? undefined : "Formato esperado: aaaa-mm"
		});
		if (!value) {
			return;
		}
		const [nyear, nmonth] = value.trim().split("-").map(n => parseInt(n, 10));
		apiManager.execute(dedicationCtrl.duplicateDedication(nyear, nmonth, selectedDedicationIds))
		.then(created => {
			apiManager.enqueueSnackbar(`Se han duplicado ${created.length} dedicaciones a ${value}`, {variant: "success"});
			setSelectedDedicationIds([]);
		});
	}

	const importarTimes = async () => {
		if (!await confirm({
			title: "Importar time entries",
			message: "Se traerán de Redmine los apuntes de horas de este mes y se guardarán en la extranet."
		})) {
			return;
		}
		setImportRedmine(true);
		setCreateResult(undefined);   // deja de describir el mes en cuanto cambian los apuntes
		apiManager.execute(dedicationCtrl.importRedmineDedication(year, parseInt(month)))
		.then(resp => {
			loadRedmineStat();
			const avisos = resp.warnings || [];
			setTimesWarnings(avisos);
			apiManager.enqueueSnackbar(
				`Se han actualizado ${resp.toSave?.length || 0} entradas`
				+ (avisos.length > 0 ? `, con ${avisos.length} aviso(s)` : ""),
				{variant: avisos.length > 0 ? "warning" : "success"});
		})
		.finally(() => setImportRedmine(false));
	}

	const borrarTimes = async () => {
		if (!await confirm({
			title: "Borrar time entries",
			message: "Se borrarán de la extranet todos los apuntes de horas importados de este mes.",
			confirmLabel: "Borrar",
			danger: true
		})) {
			return;
		}
		setImportRedmine(true);
		setTimesWarnings([]);
		setCreateResult(undefined);
		apiManager.execute(dedicationCtrl.removeRedmineDedication(year, parseInt(month)))
		.then(() => {
			loadRedmineStat();
			apiManager.enqueueSnackbar("Se han borrado todas las entradas", {variant: "success"});
		})
		.finally(() => setImportRedmine(false));
	}

	const crearDedicaciones = async () => {
		if (!await confirm({
			title: "Crear dedicaciones",
			message: "Se crearán las dedicaciones del mes a partir de los apuntes de horas importados. "
				+ "Las horas que se hayan ajustado a mano en el cierre se respetan."
		})) {
			return;
		}
		setCreateDedications(true);
		apiManager.execute(dedicationCtrl.createDedicationsDedication(year, parseInt(month)))
		.then(res => {
			setCreateResult(res);
			loadBilledPending();
			setReloadToken(t => t + 1);
			apiManager.enqueueSnackbar(resumenCreacion(res), {
				variant: res.status === "ERROR" ? "error" : (res.status === "WARNING" ? "warning" : "success")
			});
		})
		.finally(() => setCreateDedications(false));
	}

	/**
	 * Antes esto ocurría solo, dentro del listado de dedicaciones, en cada refresco de pantalla.
	 * Ahora que el listado no escribe, hay que pedirlo — y sólo hace falta con datos heredados.
	 */
	const repararDedicaciones = async () => {
		if (!await confirm({
			title: "Reparar dedicaciones",
			message: "Se completará el número de factura de las dedicaciones ya facturadas y se "
				+ "corregirá el cliente de las que no coincida con el de su presupuesto."
		})) {
			return;
		}
		setRepairing(true);
		apiManager.execute(dedicationCtrl.repairDedication(year, parseInt(month)))
		.then(res => {
			const total = (res.billNumbersFixed || 0) + (res.customersFixed || 0);
			apiManager.enqueueSnackbar(
				total === 0
					? "No había nada que reparar"
					: `Reparadas: ${res.billNumbersFixed} números de factura y ${res.customersFixed} clientes`,
				{variant: total === 0 ? "info" : "success"});
		})
		.finally(() => setRepairing(false));
	}

	const pdfFacturas = () => {
		setOperatingBills(true);
		apiManager.executeRaw(dedicationCtrl.pdfsDedication(year, parseInt(month)))
		.then((resp) => fileLocalSave(resp))
		.finally(() => setOperatingBills(false));
	}

	return (
		<>
			{confirmDialog}
			{promptDialog}
			{/* Rejilla CSS en lugar de Grid: ref-diseno-admin, trampas (#13893). */}
			<Box sx={{display: "grid", gap: 2, mt: 2, gridTemplateColumns: {xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))"}}}>
				<Panel title="Facturas del mes"
					   icon={<ReceiptLongRoundedIcon fontSize="small" color="action"/>}
					   action={summary && (summary.billsGenerated || 0) > 0
						   ? <SoftChip tone="error" label={`${summary.billsGenerated} generadas`}/>
						   : summary && (summary.billingPending || 0) > 0
							   ? <SoftChip tone="warning" label={`${summary.billingPending} dedicaciones por facturar`}/>
							   : summary ? <SoftChip tone="success" label="Nada pendiente"/> : undefined}>
					<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
						{summary && summary.billingPending && summary.billingPending > 0 &&
							<Button variant="contained"
									disabled={operatingBills}
									onClick={() => crearFacturas()}>
								Facturar
								({selectedDedicationIds.length > 0 ? selectedDedicationIds.length : summary.billingPending})
								dedicaciones
							</Button>
							|| ""}

						{summary && summary.billsGenerated && summary.billsGenerated > 0 && (
							<>
								<Button variant="outlined" color="error"
										disabled={operatingBills}
										onClick={() => borrarFacturas()}>
									Borrar {summary.billsGenerated} facturas
								</Button>
								<Button variant="outlined"
										disabled={operatingBills} onClick={() => pdfFacturas()}>
									PDFs
								</Button>
							</>
						) || ""}
					</Stack>
				</Panel>

				<Panel title="Herramientas" icon={<BuildRoundedIcon fontSize="small" color="action"/>}
					   subtitle={`Redmine: ${redmineStat?.count ?? "—"} apuntes, ${redmineStat?.sum?.toFixed(2) ?? "—"} horas.`}>
					<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
						<Button variant="outlined"
								size={"small"}
								disabled={selectedDedicationIds.length < 1}
								onClick={() => duplicarDedicaciones()}>
							Duplicar
						</Button>
						<Button variant="outlined"
								size={"small"}
								color="error"
								disabled={selectedDedicationIds.length < 1}
								onClick={async () => {
									if (await confirm({
										title: "Borrar dedicaciones",
										message: `Se borrarán las ${selectedDedicationIds.length} dedicaciones seleccionadas.`,
										confirmLabel: "Borrar",
										danger: true
									})) {
										apiManager.execute(dedicationCtrl.dedicationBatchDeleteDedication(selectedDedicationIds))
										.then(resp => {
											setSelectedDedicationIds([]);
											apiManager.enqueueSnackbar(resp, {variant: "success"});
										});
									}
								}}>
							Borrar selección
						</Button>
						<Button variant="outlined"
								size={"small"}
								disabled={importRedmine}
								onClick={() => importarTimes()}>
							{importRedmine && (<CircularProgress size="1rem" style={{marginRight: 6}}/>)}
							Importar times
						</Button>
						<Button variant="outlined"
								size={"small"}
								disabled={importRedmine || (redmineStat?.count || 0) == 0}
								onClick={() => borrarTimes()}>
							{importRedmine && (<CircularProgress size="1rem" style={{marginRight: 6}}/>)}
							Borrar times
						</Button>
						<Button variant="outlined"
								size={"small"}
								disabled={createDedications}
								onClick={() => crearDedicaciones()}>
							{createDedications && (<CircularProgress size="1rem" style={{marginRight: 6}}/>)}
							Crear dedicaciones
						</Button>
						<Button variant="outlined"
								size={"small"}
								disabled={repairing}
								title={"Completa el número de factura y el cliente de dedicaciones antiguas"}
								onClick={() => repararDedicaciones()}>
							{repairing && (<CircularProgress size="1rem" style={{marginRight: 6}}/>)}
							Reparar
						</Button>
					</Stack>
					<Stack direction="row" spacing={1} sx={{mt: 2, color: "text.secondary"}}>
						<InfoOutlinedIcon sx={{fontSize: 16, mt: "2px"}}/>
						<Typography variant="caption">
							El rol solo aparece en las empresas con <strong>facturas anónimas</strong>, así:{" "}
							<code>P-3232 - &lt;rol_persona&gt;</code>
						</Typography>
					</Stack>
				</Panel>
			</Box>

			{timesWarnings.length > 0 && (
				<Alert sx={{mt: 3}} severity="warning" onClose={() => setTimesWarnings([])}>
					<AlertTitle>Avisos de la importación de Redmine</AlertTitle>
					<ul style={{margin: 0, paddingLeft: "1.2em"}}>
						{timesWarnings.map((w, i) => <li key={i}>{w}</li>)}
					</ul>
				</Alert>
			)}

			{createResult && <ResultadoCreacion res={createResult} onClose={() => setCreateResult(undefined)}/>}

			<OccupationImputation occupation={occupation} month={month}/>

			<DedicationTable month={month}
							 year={year}
							 reloadToken={reloadToken}
							 showPerson={true}
							 personId={undefined}
							 occupation={occupation}
							 dedicable={dedicable}/>
		</>
	);
}

/** Una línea con lo que ha hecho «Crear dedicaciones», que antes siempre decía «se han creado N». */
const resumenCreacion = (res: DedicationCreateResultDto): string => {
	if (!res.monthTotal) {
		// Un «sin cambios» en verde sobre un mes vacío se lee como que ha ido bien, y lo que ha
		// pasado es que no hay apuntes que convertir: normalmente falta importar los times.
		return "No hay apuntes de horas importados para este mes: no se ha creado ninguna dedicación";
	}

	// Cada dedicación cae en UNO de estos cuatro grupos, que por eso suman el total tocado.
	// `rebound` no es un quinto grupo: es un apellido de algunas de las de arriba —las que además
	// han cambiado de presupuesto— y listarlo junto a ellas hacía que el resumen contara de más.
	const partes: string[] = [];
	if (res.created) partes.push(`${res.created} creadas`);
	if (res.updated) partes.push(`${res.updated} actualizadas`);
	if (res.adjustmentsKept) partes.push(`${res.adjustmentsKept} con ajuste manual respetado`);
	if (res.unchanged) partes.push(`${res.unchanged} sin cambios`);
	if (partes.length === 0) partes.push("sin cambios");

	let texto = `Dedicaciones: ${partes.join(", ")}`;
	if (res.rebound) {
		texto += ` (${res.rebound} de ellas, reenganchadas a otro presupuesto)`;
	}

	const pendientes: string[] = [];
	if (res.vanished) pendientes.push(`${res.vanished} ya no las respalda Redmine`);
	if (res.billedBlocked) pendientes.push(`${res.billedBlocked} bloqueadas por factura`);
	if (pendientes.length > 0) {
		texto += `. Para revisar: ${pendientes.join(" y ")}`;
	}
	return texto;
}

interface ResultadoCreacionProps {
	res: DedicationCreateResultDto;
	onClose: () => void;
}

/**
 * Lo que la importación no ha podido aplicar —ajustes que ha respetado, dedicaciones que ya no
 * están en Redmine, facturadas que han cambiado— se queda en pantalla hasta que se cierra. En un
 * snackbar no da tiempo a leerlo, y hasta ahora solo salía en el log del servidor.
 */
const ResultadoCreacion = ({res, onClose}: ResultadoCreacionProps) => (
	<Alert sx={{mt: 3}}
		   onClose={onClose}
		   severity={res.status === "ERROR" ? "error" : (res.status === "WARNING" ? "warning" : "success")}>
		<AlertTitle>{resumenCreacion(res)}</AlertTitle>
		{(res.warnings || []).length > 0 && (
			<ul style={{margin: "0.5em 0 0", paddingLeft: "1.2em"}}>
				{(res.warnings || []).map((w, i) => <li key={i}>{w}</li>)}
			</ul>
		)}
		<div style={{fontSize: "0.85em", marginTop: "0.5em"}}>
			El mes tiene {res.monthTotal} dedicaciones. Las horas ajustadas a mano no se reescriben:
			para volver a las de Redmine, edítalas y déjalas con el valor que él trae. Lo que está
			facturado no se toca, así que si Redmine trae otra cifra hay que abonar la factura y
			volver a importar.
		</div>
	</Alert>
);
